import React, { useState, useContext, useMemo } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { gql, useQuery, useMutation } from "@apollo/client";
import Spinner from "../../components/Spinner";
import StarekContext from "../../context/StarekContext";
import EntityOperationBar from "../../components/EntityOperationBar";
import "../../starek.css";
import DataTable from "react-data-table-component";
import moment from "moment";
import { deleteWebhook } from "../../services/RepositoryService";
import { useNavigate } from "react-router-dom";

const Webhooks = () => {
    const starekContext = useContext(StarekContext);
    const navigate = useNavigate();

    const GET_WEBHOOKS_LIST = gql`
        query GetWebhooks($plantId: Int!) {
            webhooks(where: { plant_id: { equals: $plantId } }) {
                id
                name
                description
                event
                method
                url
                created_at
            }
        }
    `;

    const { data, loading, error, refetch } = useQuery(GET_WEBHOOKS_LIST, {
        client: starekContext.engineClient,
        variables: { plantId: starekContext.lastUsedPlant.id },
        fetchPolicy: "network-only",
    });

    const [filterText, setFilterText] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteDone, setDeleteDone] = useState(0);
    const [toggleCleared, setToggleCleared] = useState(false);

    const webhooks = data?.webhooks ?? [];

    const filteredItems = useMemo(() => {
        const q = filterText.trim().toLowerCase();
        if (!q) return webhooks;
        return webhooks.filter(
            (item) =>
                item.name?.toLowerCase().includes(q) ||
                item.description?.toLowerCase().includes(q) ||
                item.event?.toLowerCase().includes(q) ||
                item.url?.toLowerCase().includes(q)
        );
    }, [webhooks, filterText]);

    const handleAdd = () => {
        navigate("/webhooks/0");
    };

    const handleEdit = () => {
        if (selectedRows.length !== 1) return;
        navigate(`/webhooks/${selectedRows[0].id}`);
    };

    const handleBulkDelete = async () => {
        setIsDeleting(true);
        setDeleteDone(0);
        try {
            for (const webhook of selectedRows) {
                await deleteWebhook(webhook.id);
                setDeleteDone((n) => n + 1);
            }
            setSelectedRows([]);
            setToggleCleared((p) => !p);
            await refetch();
        } catch (error) {
            starekContext.showError(error);
        } finally {
            setIsDeleting(false);
            setDeleteDone(0);
        }
    };

    const columns = [
        {
            name: "Name",
            selector: (row) => row.name,
            sortable: true,
            cell: (row) => (
                <div
                    className="d-flex align-items-center"
                    style={{ cursor: "pointer" }}
                    onClick={() => navigate(`/webhooks/${row.id}`)}
                >
                    <span className="w-30px h-30px me-2 d-flex align-items-center justify-content-center rounded-circle border bg-color-f2f2f2">
                        <i className="fa fa-link"></i>
                    </span>
                    <span>{row.name}</span>
                </div>
            ),
        },
        {
            name: "Description",
            selector: (row) => row.description,
            sortable: true,
        },
        {
            name: "Event",
            selector: (row) => row.event,
            sortable: true,
        },
        {
            name: "Method",
            selector: (row) => row.method,
            sortable: true,
            width: "120px",
        },
        {
            name: "URL",
            selector: (row) => row.url,
            sortable: true,
            cell: (row) => (
                <span
                    title={row.url}
                    style={{
                        maxWidth: "300px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "inline-block",
                    }}
                >
                    {row.url}
                </span>
            ),
        },
        {
            name: "Created At",
            selector: (row) =>
                moment.utc(row.created_at).format("YYYY-MM-DD HH:mm:ss"),
            sortable: true,
            width: "180px",
        },
    ];

    const customStyles = {
        headCells: { style: { fontWeight: "bold" } },
    };

    return loading ? (
        <div
            className="d-flex flex-column justify-content-center align-items-center"
            style={{ minHeight: "100vh" }}
        >
            <div className="text-xs d-flex justify-content-center">
                <i className="fa fa-link fa-10x text-success"></i>
            </div>
            <Spinner />
        </div>
    ) : error ? (
        <div className="tw-text-center tw-mt-10 tw-text-red-600">
            Failed to load webhooks: {error.message}
        </div>
    ) : (
        <div className="container-fluid">
            {webhooks && (
                <>
                    <EntityOperationBar
                        entity={{ singular: "Webhook", plural: "Webhooks" }}
                        allowSearch={true}
                        allowAdd={true}
                        allowDelete={true}
                        allowEdit={true}
                        selectedRows={selectedRows}
                        isDeleting={isDeleting}
                        deleteDone={deleteDone}
                        onSearch={(text) => setFilterText(text)}
                        onAdd={handleAdd}
                        onEdit={handleEdit}
                        onDelete={handleBulkDelete}
                    >
                        <EntityOperationBar.View
                            mode="list"
                            icon="fa fa-list"
                            tooltip="List View"
                        >
                            <DataTable
                                columns={columns}
                                data={filteredItems}
                                selectableRows
                                onSelectedRowsChange={({ selectedRows }) =>
                                    setSelectedRows(selectedRows)
                                }
                                clearSelectedRows={toggleCleared}
                                pagination
                                highlightOnHover
                                striped
                                responsive
                                customStyles={customStyles}
                            />
                        </EntityOperationBar.View>
                    </EntityOperationBar>
                </>
            )}
        </div>
    );
};

export { Webhooks };
